import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStorage } from '../../../hooks';
import {
  Topbar,
  Navbar,
  DashboardFooter,
  Breadcrumb,
} from '../../../components';
import { Container, Content } from '../../../styles/lib/grid';
import {
  TabBadge,
  TabSelector,
  TabsNavigation,
  TabsSelector,
} from '../../../styles/lib/tabs';
import { TitleContent } from '../../../styles/lib/typography';
import { Dealers } from '../Dealers/Dealers';
import { NewDealers } from '../Dealers/NewDealers';
import { ActivityHistory } from '../../AccessControl/ActivityHistory';

const TABS = {
  DEALERS: 'dealers',
  NEW_DEALERS: 'new-dealers',
  ACTIVITY_HISTORY: 'activity-history',
} as const;

const PATHS = {
  PANEL: '/painel',
  ACCESS_CONTROL: '/gestao-de-revendas/novos-revendedores',
  DEALERS: '/gestao-de-revendas/revendedores',
  NEW_DEALERS: '/gestao-de-revendas/novos-revendedores',
  ACTIVITY_HISTORY: '/gestao-de-revendas/historico-de-atividades',
} as const;

type TabType = keyof typeof TABS;

export const DealerTabNavigation: React.FC = () => {
  const { getItemOnStorage } = useStorage();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const activeTab: TabType = useMemo(() => {
    if (pathname.includes('novos-revendedores')) return 'NEW_DEALERS';
    if (pathname.includes('revendedores')) return 'DEALERS';
    if (pathname.includes('historico-de-atividades')) return 'ACTIVITY_HISTORY';
    return 'NEW_DEALERS';
  }, [pathname]);

  const handleTabClick = (tab: TabType) => {
    navigate(PATHS[tab]);
  };

  const user = useMemo(
    () => JSON.parse(getItemOnStorage('currentUser') || '{}'),
    [getItemOnStorage],
  );
  const [totalEmAnalise, setTotalEmAnalise] = useState<string | null>(null);

  const pageTitle = useMemo(() => {
    switch (activeTab) {
      case 'DEALERS':
        return 'Gestão de revendas';
      case 'NEW_DEALERS':
        return 'Novos revendedores';
      case 'ACTIVITY_HISTORY':
        return 'Histórico de Atividades';
      default:
        return '';
    }
  }, [activeTab]);

  const breadcrumbPaths = useMemo(
    () => [
      { title: 'Meu Painel', link: PATHS.PANEL },
      { title: 'Gestão de revendas', link: PATHS.ACCESS_CONTROL },
      { title: pageTitle, link: PATHS[activeTab] },
    ],
    [activeTab, pageTitle],
  );

  return (
    <Container>
      <Topbar />
      <Navbar />
      <Content className="modify-content">
        <Breadcrumb paths={breadcrumbPaths} />
        <TitleContent style={{ marginBottom: '3%' }}>{pageTitle}</TitleContent>

        <TabsNavigation>
          <TabsSelector className="secondary">
            <TabSelector
              className={activeTab === 'NEW_DEALERS' ? 'active' : ''}
              onClick={() => handleTabClick('NEW_DEALERS')}
            >
              Novos revendedores
              <TabBadge
                className={activeTab === 'NEW_DEALERS' ? '' : 'inactive'}
              >
                {totalEmAnalise || '-'}
              </TabBadge>
            </TabSelector>
            <TabSelector
              className={activeTab === 'DEALERS' ? 'active' : ''}
              onClick={() => handleTabClick('DEALERS')}
            >
              Revendedores
            </TabSelector>
            <TabSelector
              className={activeTab === 'ACTIVITY_HISTORY' ? 'active' : ''}
              onClick={() => handleTabClick('ACTIVITY_HISTORY')}
            >
              Histórico de Atividades
            </TabSelector>
          </TabsSelector>
        </TabsNavigation>
      </Content>

      {activeTab === 'NEW_DEALERS' && (
        <NewDealers setTotalEmAnalise={setTotalEmAnalise} />
      )}
      {activeTab === 'DEALERS' && <Dealers />}
      {activeTab === 'ACTIVITY_HISTORY' && <ActivityHistory />}

      <DashboardFooter />
    </Container>
  );
};
